// @ts-check
/** @typedef {import('../types/api').ProductImage} ProductImage */

/**
 * Change detection for already-saved product images, so the product edit modal only
 * PUTs the fields that actually moved (alt text, and display_order for up/down reorders).
 */

/** @typedef {{ alt_text: string, display_order?: number }} ImageSnapshot */

/**
 * Snapshot of what the server holds, keyed by image id. Re-take it after every save,
 * otherwise reverting an edit to its original value would look like "no change".
 * @param {ProductImage[]} images
 * @returns {Record<string, ImageSnapshot>}
 */
export function imageSnapshotById(images) {
  return Object.fromEntries(
    images.map((img) => [img.id, { alt_text: img.alt_text || '', display_order: img.display_order }]),
  );
}

/**
 * Fields of `image` that differ from the server snapshot, or null when nothing changed.
 * With no snapshot the image is treated as fully changed.
 * @param {ProductImage} image
 * @param {ImageSnapshot | undefined} original
 * @returns {{ alt_text?: string, display_order?: number } | null}
 */
export function changedImageFields(image, original) {
  /** @type {{ alt_text?: string, display_order?: number }} */
  const changes = {};
  if ((image.alt_text || '') !== original?.alt_text) changes.alt_text = image.alt_text;
  if (image.display_order !== original?.display_order) changes.display_order = image.display_order;
  return Object.keys(changes).length ? changes : null;
}
